import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { callApi } from "../api/api";
import useRBAC from "../hooks/useRBAC";
import { ArrowLeft, PlusCircle, Trash, RefreshCw, Play, Pause } from "lucide-react";

const THEME = "rgb(37, 99, 235)";
const THEME_LIGHT = "rgba(37, 99, 235, 0.08)";

/* ─────────────────────────────────────────────────────────────
   CUSTOM SEARCHABLE SELECT COMPONENT
   ───────────────────────────────────────────────────────────── */
function SearchableSelect({
  options = [],       // [{ value, label, sub }]
  value,
  onChange,
  placeholder = "Select…",
  disabled = false,
  loading = false,
  id,
  showSearch = false, // added to selectively show the search bar
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const optionsRefs = useRef([]);

  const selected = options.find((o) => o.value === value);

  const filtered = (showSearch && search.trim())
    ? options.filter(
      (o) =>
        o.label.toLowerCase().includes(search.toLowerCase()) ||
        (o.sub && o.sub.toLowerCase().includes(search.toLowerCase()))
    )
    : options;

  /* close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* focus handling on open */
  useEffect(() => {
    if (open) {
      const selectedIndex = filtered.findIndex((o) => o.value === value);
      setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);

      if (showSearch && searchRef.current) {
        searchRef.current.focus();
      }
    } else {
      setFocusedIndex(-1);
    }
  }, [open, showSearch, filtered, value]);

  /* scroll focused option into view */
  useEffect(() => {
    if (focusedIndex >= 0 && optionsRefs.current[focusedIndex]) {
      optionsRefs.current[focusedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [focusedIndex]);

  const handleToggle = () => {
    if (disabled || loading) return;
    setOpen((prev) => !prev);
    setSearch("");
  };

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setSearch("");
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (disabled || loading) return;

    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (filtered.length > 0) {
          setFocusedIndex((prev) => (prev + 1) % filtered.length);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (filtered.length > 0) {
          setFocusedIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
        }
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filtered.length) {
          handleSelect(filtered[focusedIndex].value);
        } else if (filtered.length > 0) {
          handleSelect(filtered[0].value);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setSearch("");
        if (triggerRef.current) {
          triggerRef.current.focus();
        }
        break;
      case "Tab":
        setOpen(false);
        setSearch("");
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapRef} onKeyDown={handleKeyDown} style={{ position: "relative", width: "100%", zIndex: open ? 1000 : 1 }} id={id}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        className="ado-focusable"
        onClick={handleToggle}
        disabled={disabled || loading}
        style={{
          ...ss.trigger,
          opacity: disabled ? 0.55 : 1,
          cursor: disabled || loading ? "not-allowed" : "pointer",
          borderColor: open ? THEME : "#cbd5e1",
          boxShadow: open ? `0 0 0 3px ${THEME_LIGHT}` : "none",
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={ss.triggerText}>
          {loading
            ? "Loading…"
            : selected
              ? (
                <span>
                  {selected.label}
                  {selected.sub && (
                    <span style={ss.triggerSub}> · {selected.sub}</span>
                  )}
                </span>
              )
              : <span style={{ color: "#94a3b8" }}>{placeholder}</span>}
        </span>
        <svg
          style={{
            ...ss.chevron,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="5 8 10 13 15 8" />
        </svg>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={ss.panel} role="listbox">
          {/* Search bar */}
          {showSearch && (
            <div style={ss.searchWrap}>
              <svg style={ss.searchIcon} viewBox="0 0 20 20" fill="none" stroke="#94a3b8" strokeWidth="2">
                <circle cx="8.5" cy="8.5" r="5.5" />
                <line x1="13" y1="13" x2="17" y2="17" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                style={ss.searchInput}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  style={ss.clearBtn}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          )}

          {/* Options list */}
          <div style={ss.optionList}>
            {filtered.length === 0 ? (
              <div style={ss.emptyMsg}>No results found</div>
            ) : (
              filtered.map((o, idx) => {
                const isActive = o.value === value;
                const isFocused = idx === focusedIndex;
                return (
                  <button
                    key={o.value}
                    ref={(el) => (optionsRefs.current[idx] = el)}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    tabIndex={-1}
                    onClick={() => handleSelect(o.value)}
                    onMouseEnter={() => setFocusedIndex(idx)}
                    style={{
                      ...ss.option,
                      background: isActive ? THEME_LIGHT : "transparent",
                      outline: isFocused ? `1px solid ${THEME}` : "none",
                      outlineOffset: "-1px",
                      color: (isActive || isFocused) ? THEME : "#0f172a",
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    <span style={ss.optionLabel}>{o.label}</span>
                    {o.sub && <span style={ss.optionSub}>{o.sub}</span>}
                    {isActive && (
                      <svg style={ss.checkIcon} viewBox="0 0 20 20" fill={THEME}>
                        <path d="M5 10l4 4 6-8" stroke={THEME} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const ss = {
  trigger: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "10px 13px",
    border: "1px solid #cbd5e1",
    borderRadius: "9px",
    fontSize: "14px",
    background: "#ffffff",
    color: "#0f172a",
    fontFamily: "inherit",
    cursor: "pointer",
    textAlign: "left",
    transition: "border-color 0.15s, box-shadow 0.15s",
    height: "44px",
    boxSizing: "border-box",
    gap: "8px",
  },
  triggerText: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "14px",
  },
  triggerSub: {
    color: "#94a3b8",
    fontSize: "12px",
  },
  chevron: {
    width: 16,
    height: 16,
    flexShrink: 0,
    color: "#64748b",
    transition: "transform 0.2s ease",
  },
  panel: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    zIndex: 9999,
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    boxShadow: "0 8px 32px rgba(15,23,42,0.12), 0 2px 8px rgba(15,23,42,0.06)",
    overflow: "hidden",
  },
  searchWrap: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    background: "#f8fafc",
  },
  searchIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    border: "none",
    background: "transparent",
    outline: "none",
    fontSize: "13px",
    color: "#0f172a",
    fontFamily: "inherit",
  },
  clearBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94a3b8",
    fontSize: "18px",
    lineHeight: 1,
    padding: "0 2px",
    display: "flex",
    alignItems: "center",
  },
  optionList: {
    maxHeight: "220px",
    overflowY: "auto",
    padding: "6px",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    padding: "9px 10px",
    borderRadius: "7px",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    fontFamily: "inherit",
    textAlign: "left",
    transition: "background 0.1s",
    boxSizing: "border-box",
  },
  optionLabel: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  optionSub: {
    fontSize: "11px",
    color: "#94a3b8",
    flexShrink: 0,
  },
  checkIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  emptyMsg: {
    padding: "16px 12px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: "13px",
  },
};

/* ─────────────────────────────────────────────────────────────
   CUSTOM TIME PICKER COMPONENT
   Two scrollable columns: hours (00–23) and minutes (00–59).
   Matches the design with blue highlighted selected values.
   ───────────────────────────────────────────────────────────── */
function TimePicker({ value = "12:00", onChange }) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false); // flip direction
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);

  const [hh, mm] = value.split(":").map(Number);

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const pad = (n) => String(n).padStart(2, "0");

  /* Close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* Scroll selected item to top of column when opened */
  useEffect(() => {
    if (!open) return;
    const ITEM_H = 40;
    if (hourRef.current) hourRef.current.scrollTop = hh * ITEM_H;
    if (minuteRef.current) minuteRef.current.scrollTop = mm * ITEM_H;
  }, [open, hh, mm]);

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      // Detect whether to flip upward
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUp(spaceBelow < 260); // flip if less than 260px below
    }
    setOpen((p) => !p);
  };

  const setHour = (h) => onChange(`${pad(h)}:${pad(mm)}`);
  const setMinute = (m) => onChange(`${pad(hh)}:${pad(m)}`);

  const panelPosition = openUp
    ? { bottom: "calc(100% + 6px)", top: "auto" }
    : { top: "calc(100% + 6px)", bottom: "auto" };

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      <style>{`
        .tp-scroll::-webkit-scrollbar { display: none; }
        .tp-item:hover { background: rgba(37,99,235,0.08) !important; color: rgb(37,99,235) !important; }
      `}</style>

      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: "10px 13px",
          border: `1px solid ${open ? THEME : "#cbd5e1"}`,
          borderRadius: "9px",
          fontSize: "14px",
          fontWeight: "400",
          background: "#ffffff",
          color: "#0f172a",
          fontFamily: "inherit",
          cursor: "pointer",
          height: "44px",
          boxSizing: "border-box",
          boxShadow: open ? `0 0 0 3px ${THEME_LIGHT}` : "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
        }}
      >
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {pad(hh)}:{pad(mm)}
        </span>
        <svg viewBox="0 0 24 24" fill="none" stroke={open ? THEME : "#94a3b8"} strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
          style={{ width: 16, height: 16, flexShrink: 0, transition: "stroke 0.15s" }}>
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      </button>

      {/* Dropdown panel — flips up if not enough space below */}
      {open && (
        <div style={{
          position: "absolute",
          ...panelPosition,
          left: 0,
          right: 0,
          zIndex: 99999,
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: "14px",
          boxShadow: "0 12px 40px rgba(15,23,42,0.15), 0 2px 8px rgba(15,23,42,0.08)",
          padding: "8px 6px 6px",
          display: "flex",
          gap: "4px",
          boxSizing: "border-box",
        }}>
          {/* Hours column */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <div style={{
              fontSize: "10px", fontWeight: "800", color: "#94a3b8",
              textAlign: "center", letterSpacing: "0.08em",
              marginBottom: "4px", textTransform: "uppercase", padding: "2px 0 6px",
              borderBottom: "1px solid #f1f5f9",
            }}>HH</div>
            <div ref={hourRef} className="tp-scroll"
              style={{ maxHeight: "180px", overflowY: "auto", scrollbarWidth: "none", padding: "4px 2px" }}>
              {hours.map((h) => (
                <div
                  key={h}
                  className={h === hh ? "" : "tp-item"}
                  onClick={() => setHour(h)}
                  style={{
                    height: "40px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "14px", fontWeight: h === hh ? "700" : "400",
                    cursor: "pointer", borderRadius: "8px",
                    background: h === hh ? THEME : "transparent",
                    color: h === hh ? "#fff" : "#0f172a",
                    transition: "background 0.1s, color 0.1s",
                    userSelect: "none",
                  }}
                >
                  {pad(h)}
                </div>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div style={{ width: "1px", background: "#f1f5f9", margin: "24px 2px 0" }} />

          {/* Minutes column */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <div style={{
              fontSize: "10px", fontWeight: "800", color: "#94a3b8",
              textAlign: "center", letterSpacing: "0.08em",
              marginBottom: "4px", textTransform: "uppercase", padding: "2px 0 6px",
              borderBottom: "1px solid #f1f5f9",
            }}>MM</div>
            <div ref={minuteRef} className="tp-scroll"
              style={{ maxHeight: "180px", overflowY: "auto", scrollbarWidth: "none", padding: "4px 2px" }}>
              {minutes.map((m) => (
                <div
                  key={m}
                  className={m === mm ? "" : "tp-item"}
                  onClick={() => setMinute(m)}
                  style={{
                    height: "40px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "14px", fontWeight: m === mm ? "700" : "400",
                    cursor: "pointer", borderRadius: "8px",
                    background: m === mm ? THEME : "transparent",
                    color: m === mm ? "#fff" : "#0f172a",
                    transition: "background 0.1s, color 0.1s",
                    userSelect: "none",
                  }}
                >
                  {pad(m)}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
   ───────────────────────────────────────────────────────────── */
export default function Scheduler({ onBack }) {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const { isAdmin } = useRBAC();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [triggeringJobId, setTriggeringJobId] = useState(null); // tracks which job is currently running

  // Form State
  const [selectedJobType, setSelectedJobType] = useState("ETA Missed Alert");
  const [customJobName, setCustomJobName] = useState("");
  const [frequency, setFrequency] = useState("Daily");

  // Target Organisation & Project for Create Assignments
  const [orgProjects, setOrgProjects] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [iterations, setIterations] = useState([]);
  const [selectedIteration, setSelectedIteration] = useState("");
  const [iterationsLoading, setIterationsLoading] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);

  // Report Modal States
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportItems] = useState([]);
  const [reportTitle] = useState("");

  // Custom Scheduler Form Configurations
  const [intervalMinutes, setIntervalMinutes] = useState(15);
  const [jobTime, setJobTime] = useState("12:00");
  const [jobDayOfWeek, setJobDayOfWeek] = useState("Monday");
  const [jobDayOfMonth, setJobDayOfMonth] = useState("1");
  const [jobDate, setJobDate] = useState(() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Workflow Priority Auto-Matching & Enterprise Error States
  const [assignedRoles, setAssignedRoles] = useState([]);
  const [workflowError, setWorkflowError] = useState("");

  /* auto-dismiss success banner after 5s */
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(""), 5000);
    return () => clearTimeout(t);
  }, [success]);

  /* Fetch Workflow Priority Roles on Mount */
  useEffect(() => {
    const fetchWorkflowPriority = async () => {
      try {
        const res = await callApi(instance, accounts, "GET", "/api/assignments/workflow-orders");
        const data = res?.data?.data || res?.data || {};
        const assigned = Array.isArray(data.assignedRoles) ? data.assignedRoles : [];
        setAssignedRoles(assigned);
      } catch (e) {
        console.error("Failed to load workflow priority order:", e);
      }
    };
    fetchWorkflowPriority();
  }, [instance, accounts]);

  /* Fetch Workflow Priority Roles on Mount */

  useEffect(() => {
    const loadOrgs = async () => {
      try {
        setOrgLoading(true);
        const res = await callApi(instance, accounts, "GET", "/api/assignments/organizations/projects");
        const data = res?.data?.data || res?.data || [];
        const orgList = Array.isArray(data) ? data : [];
        setOrgProjects(orgList);
        if (orgList.length > 0) {
          const firstOrg = orgList[0];
          setSelectedOrg(firstOrg.organization);
        }
      } catch (err) {
        console.error("Failed to load organisations & projects for scheduler:", err);
      } finally {
        setOrgLoading(false);
      }
    };
    loadOrgs();
  }, [instance, accounts]);

  /* Auto-select default Project when Organisation or Job Type changes */
  useEffect(() => {
    setWorkflowError("");
    if (!selectedOrg) {
      setSelectedProject("");
      return;
    }

    const orgObj = orgProjects.find((o) => o.organization === selectedOrg);
    const projectsList = orgObj?.projects || [];

    if (projectsList.length > 0 && !selectedProject) {
      const matchProj = projectsList.find(p => (p.name || p) === "CFPTest") || projectsList[0];
      setSelectedProject(matchProj.name || matchProj);
    }
  }, [selectedOrg, orgProjects, selectedJobType, selectedProject]);

  useEffect(() => {
    const loadIterations = async () => {
      if (!selectedOrg || !selectedProject) {
        setIterations([]);
        setSelectedIteration("");
        return;
      }
      try {
        setIterationsLoading(true);
        const res = await callApi(
          instance, accounts, "GET",
          `/api/assignments/iterations?organization=${encodeURIComponent(selectedOrg)}&project=${encodeURIComponent(selectedProject)}&_t=${Date.now()}`
        );
        const data = res?.data?.data || res?.data || [];
        const iterList = Array.isArray(data) ? data : [];
        setIterations(iterList);

        if (iterList.length > 0) {
          const todayStr = new Date().toISOString().substring(0, 10);
          const today = new Date(todayStr);

          // 1. Try to find current/ongoing sprint by Azure DevOps timeFrame === "current" or active date range
          const currentSprint = iterList.find((it) => {
            if (it.attributes?.timeFrame === "current" || it.timeFrame === "current" || it.isCurrent) return true;
            const startDateStr = it.attributes?.startDate || it.startDate;
            const finishDateStr = it.attributes?.finishDate || it.finishDate;
            if (startDateStr && finishDateStr) {
              const start = new Date(startDateStr.substring(0, 10));
              const finish = new Date(finishDateStr.substring(0, 10));
              if (!isNaN(start.getTime()) && !isNaN(finish.getTime())) {
                return today >= start && today <= finish;
              }
            }
            return false;
          });

          if (currentSprint) {
            setSelectedIteration(currentSprint.path || currentSprint.name);
          } else {
            // 2. Fallback to top sprint (backend sorts current/latest sprint first), excluding non-sprint folders like "Past tickets"
            const bestSprint = iterList.find((it) => {
              const name = (it.name || "").toLowerCase();
              return (
                it.attributes?.timeFrame === "current" ||
                it.attributes?.timeFrame === "future" ||
                (name.includes("sprint") && !name.includes("past"))
              );
            }) || iterList[0];
            setSelectedIteration(bestSprint.path || bestSprint.name);
          }
        } else {
          setSelectedIteration("");
        }
      } catch (err) {
        console.error("Failed to load iterations for scheduler:", err);
      } finally {
        setIterationsLoading(false);
      }
    };
    loadIterations();
  }, [selectedOrg, selectedProject, instance, accounts]);

  const loadJobs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await callApi(instance, accounts, "GET", `/api/assignments/scheduler?includeDeleted=${showDeleted}&_t=${Date.now()}`);
      const data = res?.data?.data || res?.data || [];
      const list = Array.isArray(data) ? data : [];
      setJobs(list.filter((j) => showDeleted || (!j.isDeleted && j.isActive !== false && j.status !== "Deleted")));
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to load scheduled jobs.");
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, showDeleted]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalJobName = selectedJobType === "Custom" ? customJobName.trim() : selectedJobType;
    if (!finalJobName) {
      setError("Job Name is required.");
      return;
    }

    let computedExpression = "";
    if (frequency === "Interval") {
      const mins = parseInt(intervalMinutes, 10) || 15;
      computedExpression = `Every ${mins} minute(s)`;
    } else if (frequency === "Now") {
      computedExpression = "Runs immediately";
    } else if (frequency === "Daily") {
      computedExpression = `Daily at ${jobTime}`;
    } else if (frequency === "Weekly") {
      computedExpression = `Weekly on ${jobDayOfWeek} at ${jobTime}`;
    } else if (frequency === "Monthly") {
      computedExpression = `Monthly on day ${jobDayOfMonth} at ${jobTime}`;
    } else if (frequency === "Annually") {
      computedExpression = `Annually on ${jobDate} at ${jobTime}`;
    } else if (frequency === "Custom") {
      computedExpression = `Custom run on ${jobDate} at ${jobTime}`;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      // Step 1 — Create the job
      const createRes = await callApi(instance, accounts, "POST", "/api/assignments/scheduler", {
        jobName: finalJobName,
        frequency,
        customExpression: computedExpression,
        targetOrg: (selectedJobType === "Auto-Assignment" || selectedJobType === "Create Assignments") ? selectedOrg : undefined,
        targetProject: (selectedJobType === "Auto-Assignment" || selectedJobType === "Create Assignments") ? selectedProject : undefined,
        targetIteration: (selectedJobType === "Auto-Assignment" || selectedJobType === "Create Assignments") ? selectedIteration : undefined,
      });

      const newJob = createRes?.data?.data || createRes?.data || {};
      const newJobId = newJob.jobId;

      // Reset form immediately
      setSelectedJobType("ETA Missed Alert");
      setCustomJobName("");
      setFrequency("Daily");
      setIntervalMinutes(15);
      setSelectedOrg("");
      setSelectedProject("");
      setSelectedIteration("");
      setJobTime("12:00");
      setJobDayOfWeek("Monday");
      setJobDayOfMonth("1");
      setJobDate(() => {
        const today = new Date();
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, "0");
        const d = String(today.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      });

      loadJobs(); // refresh table to show new job

      // Step 2 — Auto-trigger ONLY for "Now" frequency
      if (frequency === "Now" && newJobId) {
        setSuccess(`Job "${finalJobName}" created. Running now…`);
        setTriggeringJobId(newJobId);

        try {
          await callApi(
            instance, accounts,
            "POST",
            `/api/assignments/scheduler/${newJobId}/trigger`,
            null,
            { timeout: 120000 }
          );
          const msg = `Job "${finalJobName}" executed successfully.`;
          setSuccess(msg);

          // Refresh bell badge
          window.dispatchEvent(new CustomEvent("eta-alert-triggered"));
        } catch (triggerErr) {
          console.error("Auto-trigger failed:", triggerErr);
          setError(triggerErr?.message || "Job created but failed to auto-run. Use the Play button.");
        } finally {
          setTriggeringJobId(null);
        }
      } else {
        // For scheduled frequencies (Daily/Weekly/Monthly etc.) — just confirm registration
        setSuccess(`Job "${finalJobName}" scheduled successfully (${computedExpression}).`);
      }

      loadJobs(); // refresh again after trigger
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to schedule background job.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm("Are you sure you want to soft delete this scheduled job?")) return;
    try {
      setError("");
      setSuccess("");
      await callApi(instance, accounts, "DELETE", `/api/assignments/scheduler/${jobId}`);
      setSuccess("Scheduled job soft-deleted successfully.");
      loadJobs();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to delete scheduled job.");
    }
  };

  const handleToggleStatus = async (jobId) => {
    try {
      setError("");
      setSuccess("");
      await callApi(instance, accounts, "PATCH", `/api/assignments/scheduler/${jobId}/toggle`);
      loadJobs();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to toggle job status.");
    }
  };

  const handleTriggerJob = async (jobId, name) => {
    if (triggeringJobId) return; // prevent double-fire
    try {
      setError("");
      setSuccess("");
      setTriggeringJobId(jobId);

      // Raise timeout to 120s — the ETA Missed Alert job fetches from Azure DevOps
      // and sends emails, which can take 10-15 seconds per run.
      await callApi(
        instance, accounts,
        "POST",
        `/api/assignments/scheduler/${jobId}/trigger`,
        null,
        { timeout: 120000 }
      );
      const msg = `Job "${name}" executed successfully.`;
      setSuccess(msg);

      // Notify the NotificationBell to refresh its count immediately
      window.dispatchEvent(new CustomEvent("eta-alert-triggered"));

      loadJobs();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || err?.message || "Failed to trigger job execution.");
    } finally {
      setTriggeringJobId(null);
    }
  };

  const freqOptions = [
    { value: "Interval", label: "Interval (in Minutes)" },
    { value: "Now", label: "Now (One-time)" },
    { value: "Daily", label: "Daily" },
    { value: "Weekly", label: "Weekly" },
    { value: "Monthly", label: "Monthly" },
    { value: "Annually", label: "Annually" },
    { value: "Custom", label: "Custom (Cron Expression)" },
  ];

  return (
    <div style={s.page} id="ado-scheduler-container">
      <style>{`
        #ado-scheduler-container input:focus,
        #ado-scheduler-container button:focus {
          border-color: ${THEME} !important;
          box-shadow: 0 0 0 3px ${THEME_LIGHT} !important;
          outline: none !important;
        }
        .ado-action-btn {
          background: transparent;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 6px;
          border-radius: 6px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s ease;
          outline: none;
        }
        .ado-action-btn:hover {
          background-color: #f1f5f9 !important;
          color: #0f172a !important;
        }
        .ado-action-delete:hover {
          background-color: #ef4444 !important;
          color: #ffffff !important;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes successFadeOut {
          0%   { opacity: 1; transform: translateY(0); }
          70%  { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-6px); }
        }
        .success-auto-dismiss {
          animation: successFadeOut 5s ease forwards;
        }
      `}</style>

      {/* HEADER */}
      <div style={s.header}>
        <div style={s.breadcrumb}>
          <span>Administration</span> &gt;{" "}
          <span
            onClick={onBack || (() => navigate("/manage"))}
            style={{ color: "#2563eb", fontWeight: "600", cursor: "pointer" }}
            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
            title="Click to go to Manage Menu"
          >
            Manage
          </span>{" "}
          &gt; <span style={{ color: "#0f172a", fontWeight: "600" }}>Background Scheduler</span>
        </div>
        <h1 style={s.title}>Back Scheduler</h1>
        <p style={s.sub}>Schedule and manage automated background operations, trigger configurations, and periodic jobs.</p>
      </div>

      {/* ALERTS */}
      {workflowError && (
        <div style={{
          padding: "12px 16px",
          borderRadius: "10px",
          background: "#fef2f2",
          border: "1px solid #fca5a5",
          color: "#991b1b",
          fontSize: "13px",
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "20px",
          boxShadow: "0 2px 10px rgba(239, 68, 68, 0.08)",
          lineHeight: "1.4"
        }}>
          <span style={{ fontSize: "16px", flexShrink: 0 }}>⛔</span>
          <span>{workflowError}</span>
        </div>
      )}
      {error && <div style={s.errorBox}>{error}</div>}
      {success && (
        <div style={s.successBox} className="success-auto-dismiss">
          {success}
        </div>
      )}

      {/* GRID LAYOUT */}
      <div style={s.gridContainer}>
        {/* LEFT COLUMN: Configuration Form */}
        <div style={s.formColumn}>
          <div style={s.card}>
            <h3 style={s.cardTitle}>Schedule Background Job</h3>
            <form onSubmit={handleSubmit} style={s.form}>
              <div style={s.field}>
                <label style={s.label}>Job Name</label>
                <SearchableSelect
                  options={[
                    { value: "ETA Missed Alert", label: "ETA Missed Alert" },
                    { value: "Auto-Assignment", label: "Auto-Assignment" },
                    { value: "Re-Assignment", label: "Re-Assignment" },
                    { value: "Work Item Generation", label: "Work Item Generation" },
                  ]}
                  value={selectedJobType}
                  onChange={(val) => {
                    setSelectedJobType(val);
                    setError("");
                  }}
                  placeholder="Select job type…"
                  showSearch={false}
                />
              </div>

              {selectedJobType === "Re-Assignment" && (
                <div style={s.field}>
                  <label style={s.label}>Organisation</label>
                  <SearchableSelect
                    options={orgProjects.map((o) => ({ value: o.organization, label: o.organization }))}
                    value={selectedOrg}
                    onChange={(val) => {
                      setSelectedOrg(val);
                    }}
                    placeholder={orgLoading ? "Loading organisations…" : "Select organisation…"}
                    disabled={orgProjects.length <= 1 || orgLoading}
                    showSearch={false}
                  />
                </div>
              )}

              {(selectedJobType === "Auto-Assignment" || selectedJobType === "Create Assignments") && (
                <>
                  <div style={s.field}>
                    <label style={s.label}>Organisation</label>
                    <SearchableSelect
                      options={orgProjects.map((o) => ({ value: o.organization, label: o.organization }))}
                      value={selectedOrg}
                      onChange={(val) => {
                        setSelectedOrg(val);
                      }}
                      placeholder={orgLoading ? "Loading organisations…" : "Select organisation…"}
                      disabled={orgProjects.length <= 1 || orgLoading}
                      showSearch={false}
                    />
                  </div>

                  <div style={s.field}>
                    <label style={s.label}>Project</label>
                    <SearchableSelect
                      options={
                        (orgProjects.find((o) => o.organization === selectedOrg)?.projects || []).map((p) => ({
                          value: p.name,
                          label: p.name,
                        }))
                      }
                      value={selectedProject}
                      onChange={(val) => setSelectedProject(val)}
                      placeholder="Select project…"
                      disabled={!selectedOrg || ((orgProjects.find((o) => o.organization === selectedOrg)?.projects || []).length <= 1)}
                      showSearch={false}
                    />
                  </div>

                  <div style={s.field}>
                    <label style={s.label}>Sprint (Iteration)</label>
                    <SearchableSelect
                      options={iterations.map((it) => ({
                        value: it.path || it.name,
                        label: it.name,
                      }))}
                      value={selectedIteration}
                      onChange={(val) => setSelectedIteration(val)}
                      placeholder={iterationsLoading ? "Loading sprints…" : "Select sprint…"}
                      disabled={!selectedProject || iterationsLoading}
                      showSearch={false}
                    />
                    {selectedIteration && (
                      <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                        ℹ️ Automatically selected ongoing current Sprint (or latest Sprint). You can change it if needed.
                      </div>
                    )}
                  </div>
                </>
              )}

              {selectedJobType === "Custom" && (
                <div style={s.field}>
                  <label style={s.label}>Custom Job Name</label>
                  <input
                    type="text"
                    placeholder="Enter custom job name…"
                    style={s.input}
                    value={customJobName}
                    onChange={(e) => setCustomJobName(e.target.value)}
                  />
                </div>
              )}

              <div style={s.field}>
                <label style={s.label}>Frequency</label>
                <SearchableSelect
                  options={freqOptions}
                  value={frequency}
                  onChange={(val) => {
                    setFrequency(val);
                    setError("");
                  }}
                  placeholder="Select frequency…"
                  showSearch={false}
                />
              </div>

              {frequency === "Interval" && (
                <>
                  <div style={s.field}>
                    <label style={s.label}>Interval Duration (in Minutes) *</label>
                    <input
                      type="number"
                      min="1"
                      max="1440"
                      style={s.input}
                      value={intervalMinutes}
                      onChange={(e) => setIntervalMinutes(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      placeholder="e.g. 5, 10, 15, 30, 60"
                    />
                  </div>
                  <div style={{ fontSize: "12px", color: "#1e293b", background: "#f8fafc", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", lineHeight: "1.4" }}>
                    ℹ️ This job will automatically run every <strong>{intervalMinutes} minute(s)</strong> continuously until paused.
                  </div>
                </>
              )}

              {frequency === "Now" && (
                <div style={{ fontSize: "12px", color: "#1e293b", background: "#f8fafc", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", lineHeight: "1.4" }}>
                  ℹ️ This job will run immediately.
                </div>
              )}

              {frequency === "Daily" && (
                <div style={s.field}>
                  <label style={s.label}>Execution Time</label>
                  <TimePicker value={jobTime} onChange={setJobTime} />
                </div>
              )}

              {frequency === "Weekly" && (
                <>
                  <div style={s.field}>
                    <label style={s.label}>Execution Day</label>
                    <SearchableSelect
                      options={[
                        { value: "Monday", label: "Monday" },
                        { value: "Tuesday", label: "Tuesday" },
                        { value: "Wednesday", label: "Wednesday" },
                        { value: "Thursday", label: "Thursday" },
                        { value: "Friday", label: "Friday" },
                        { value: "Saturday", label: "Saturday" },
                        { value: "Sunday", label: "Sunday" },
                      ]}
                      value={jobDayOfWeek}
                      onChange={(val) => setJobDayOfWeek(val)}
                      placeholder="Select day…"
                      showSearch={false}
                    />
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Execution Time</label>
                    <TimePicker value={jobTime} onChange={setJobTime} />
                  </div>
                </>
              )}

              {frequency === "Monthly" && (
                <>
                  <div style={s.field}>
                    <label style={s.label}>Day of Month (1 - 31)</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      style={s.input}
                      value={jobDayOfMonth}
                      onChange={(e) => setJobDayOfMonth(e.target.value)}
                    />
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Execution Time</label>
                    <TimePicker value={jobTime} onChange={setJobTime} />
                  </div>
                </>
              )}

              {frequency === "Annually" && (
                <>
                  <div style={s.field}>
                    <label style={s.label}>Execution Date</label>
                    <input
                      type="date"
                      style={s.input}
                      value={jobDate}
                      onChange={(e) => setJobDate(e.target.value)}
                    />
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Execution Time</label>
                    <TimePicker value={jobTime} onChange={setJobTime} />
                  </div>
                </>
              )}

              {frequency === "Custom" && (
                <>
                  <div style={s.field}>
                    <label style={s.label}>Execution Date</label>
                    <input
                      type="date"
                      style={s.input}
                      value={jobDate}
                      onChange={(e) => setJobDate(e.target.value)}
                    />
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Execution Time</label>
                    <TimePicker value={jobTime} onChange={setJobTime} />
                  </div>
                </>
              )}

              <button
                type="submit"
                style={{
                  ...s.submitBtn,
                  opacity: (submitting || triggeringJobId !== null) ? 0.5 : 1,
                  cursor: (submitting || triggeringJobId !== null) ? "not-allowed" : "pointer",
                }}
                disabled={submitting || triggeringJobId !== null}
              >
                <PlusCircle size={15} style={{ marginRight: 6 }} />
                {submitting && !triggeringJobId
                  ? "Scheduling…"
                  : triggeringJobId
                    ? "Running…"
                    : frequency === "Now"
                      ? "Run Now"
                      : "Schedule Job"}
              </button>
            </form>
          </div>
        </div>

        {/* RIGHT COLUMN: Active Jobs Table */}
        <div style={s.tableColumn}>
          <div style={s.card}>
            <div style={s.tableHeaderRow}>
              <h3 style={s.cardTitle}>Scheduled Jobs Registry</h3>
              <button style={s.refreshBtn} onClick={loadJobs} title="Refresh list" disabled={loading}>
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              </button>
            </div>

            {jobs.length === 0 && loading ? (
              <div style={s.tableContainer}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Job Name</th>
                      <th style={s.th}>Frequency</th>
                      <th style={s.th}>Scheduled By</th>
                      <th style={s.thStatus}>Status</th>
                      <th style={s.thActions}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <tr key={`job-skel-${i}`} style={s.tr}>
                        <td style={s.td}><div style={{ ...s.skeletonBar, width: "140px" }} /></td>
                        <td style={s.td}><div style={{ ...s.skeletonBar, width: "70px" }} /></td>
                        <td style={s.td}><div style={{ ...s.skeletonBar, width: "120px" }} /></td>
                        <td style={s.tdStatus}><div style={{ ...s.skeletonBar, width: "50px", margin: "0 auto" }} /></td>
                        <td style={s.tdActions}><div style={{ ...s.skeletonBar, width: "60px", margin: "0 auto" }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : jobs.length === 0 && !loading ? (
              <div style={s.emptyBox}>No scheduled background jobs registered.</div>
            ) : (
              <div style={{ ...s.tableContainer, position: "relative" }}>
                {loading && (
                  <div style={s.overlayLoader}>
                    <RefreshCw size={20} color={THEME} style={{ animation: "spin 0.8s linear infinite" }} />
                    <span>Updating scheduled jobs…</span>
                  </div>
                )}
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Job Name</th>
                      <th style={s.th}>Frequency</th>
                      <th style={s.th}>Scheduled By</th>
                      <th style={s.thStatus}>Status</th>
                      <th style={s.thActions}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((j) => (
                      <tr key={j.jobId} style={{ ...s.tr, opacity: j.isDeleted ? 0.75 : 1, background: j.isDeleted ? "#f8fafc" : "#ffffff" }}>
                        <td style={s.tdBold}>
                          <span style={{ textDecoration: j.isDeleted ? "line-through" : "none" }}>{j.jobName}</span>
                          {j.targetOrg && (
                            <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "normal", marginTop: "2px" }}>
                              🎯 {j.targetOrg}
                              {j.targetProject ? ` / ${j.targetProject}` : ""}
                              {j.targetIteration &&
                                ` / ${j.targetIteration.replace(
                                  new RegExp("^" + (j.targetProject || "").replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&") + "[\\\\/]", "i"),
                                  ""
                                )}`}
                            </div>
                          )}
                          {j.isDeleted && (
                            <div style={{ fontSize: "11px", color: "#dc2626", fontWeight: "600", marginTop: "2px" }}>
                              🗑️ Soft-deleted by {j.deletedBy || "Admin"}
                            </div>
                          )}
                        </td>
                        <td style={s.td}>
                          {j.frequency === "Custom" || j.frequency === "Interval" ? (
                            <div>
                              <span style={{ fontWeight: "700" }}>{j.frequency}</span>
                              <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}>
                                <code>{j.customExpression}</code>
                              </div>
                            </div>
                          ) : (
                            j.frequency
                          )}
                        </td>
                        <td style={s.tdEmail}>{j.addedBy}</td>
                        <td style={s.tdStatus}>
                          <span
                            style={{
                              ...s.statusBadge,
                              background: j.isDeleted ? "#fee2e2" : j.status === "Active" ? "#dcfce7" : "#fef3c7",
                              color: j.isDeleted ? "#dc2626" : j.status === "Active" ? "#16a34a" : "#d97706",
                            }}
                          >
                            {j.isDeleted ? "Deleted" : j.status}
                          </span>
                        </td>
                        <td style={s.tdActions}>
                          {j.isDeleted ? (
                            <span style={{ fontSize: "11px", color: "#94a3b8", fontStyle: "italic", display: "block", textAlign: "center" }}>
                              Soft-Deleted
                            </span>
                          ) : (
                            <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                              {j.frequency === "Now" ? (
                                <button
                                  className="ado-action-btn"
                                  onClick={() => handleTriggerJob(j.jobId, j.jobName)}
                                  title={triggeringJobId === j.jobId ? "Running…" : "Re-run job"}
                                  disabled={triggeringJobId !== null}
                                  style={{ opacity: (triggeringJobId !== null) ? 0.45 : 1 }}
                                >
                                  {triggeringJobId === j.jobId
                                    ? <RefreshCw size={14} className="animate-spin" />
                                    : <RefreshCw size={14} />}
                                </button>
                              ) : (
                                <button
                                  className="ado-action-btn"
                                  onClick={() => handleToggleStatus(j.jobId)}
                                  title={j.status === "Active" ? "Pause execution" : "Activate execution"}
                                  disabled={triggeringJobId !== null}
                                >
                                  {j.status === "Active" ? <Pause size={14} /> : <Play size={14} />}
                                </button>
                              )}
                              <button
                                className="ado-action-btn ado-action-delete"
                                onClick={() => handleDelete(j.jobId)}
                                title="Remove job"
                              >
                                <Trash size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Bottom Left Admin View All Toggle */}
            {isAdmin && (
              <div style={{ marginTop: "auto", paddingTop: "16px", display: "flex", justifyContent: "flex-start" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#475569", fontWeight: "600", cursor: "pointer", background: "#f8fafc", padding: "6px 12px", borderRadius: "8px", border: "1px solid #cbd5e1" }}>
                  <input
                    type="checkbox"
                    checked={showDeleted}
                    onChange={(e) => setShowDeleted(e.target.checked)}
                    style={{ cursor: "pointer", accentColor: THEME }}
                  />
                  View All (Including Soft-Deleted)
                </label>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* OVERDUE REPORT MODAL */}
      {showReportModal && (
        <div style={s.modalOverlay}>
          <div style={s.modalContent}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>{reportTitle}</h3>
              <button style={s.closeBtn} onClick={() => setShowReportModal(false)}>×</button>
            </div>

            <p style={{ fontSize: "13px", color: "#64748b", margin: "0 0 16px 0" }}>
              The following work items have exceeded their scheduled target date (ETA):
            </p>

            <div style={s.modalTableContainer}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>ID</th>
                    <th style={s.th}>Title</th>
                    <th style={s.th}>Assignee</th>
                    <th style={s.th}>Target Date</th>
                    <th style={s.th}>State</th>
                  </tr>
                </thead>
                <tbody>
                  {reportItems.map((item) => (
                    <tr key={item.id} style={s.tr}>
                      <td style={s.tdBold}>#{item.id}</td>
                      <td style={s.td}>{item.title}</td>
                      <td style={s.td}>{item.assignedTo || <span style={{ color: "#94a3b8" }}>Unassigned</span>}</td>
                      <td style={{ ...s.td, color: "#dc2626", fontWeight: "600" }}>{item.targetDate}</td>
                      <td style={s.td}>{item.state}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
              <button style={s.modalCloseBtn} onClick={() => setShowReportModal(false)}>
                Close Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    boxSizing: "border-box",
    maxWidth: "100%",
    padding: "clamp(12px, 4vw, 24px)",
  },
  header: { marginBottom: "20px" },
  breadcrumb: {
    fontSize: "12px",
    color: "#94a3b8",
    marginBottom: "6px",
  },
  title: {
    margin: 0,
    fontSize: "26px",
    fontWeight: "700",
    color: "#0f172a",
  },
  sub: {
    color: "#64748b",
    marginTop: "5px",
    fontSize: "14px",
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    color: THEME,
    cursor: "pointer",
    fontWeight: "600",
    background: "none",
    border: "none",
    padding: 0,
    marginBottom: "20px",
    outline: "none",
  },
  gridContainer: {
    display: "flex",
    gap: "20px",
    flexWrap: "wrap",
    width: "100%",
    boxSizing: "border-box",
  },
  formColumn: {
    flex: "1 1 300px",
    maxWidth: "100%",
  },
  tableColumn: {
    flex: "2 1 600px",
    maxWidth: "100%",
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    padding: "clamp(16px, 4vw, 24px)",
    boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
    boxSizing: "border-box",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  cardTitle: {
    margin: "0 0 16px 0",
    fontSize: "16px",
    fontWeight: "600",
    color: "#0f172a",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  input: {
    padding: "10px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    fontSize: "14px",
    background: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    color: "#0f172a",
    height: "42px",
  },
  submitBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    background: THEME,
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "600",
    fontFamily: "inherit",
    outline: "none",
    marginTop: "8px",
    height: "42px",
    cursor: "pointer",
  },
  tableHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    flexWrap: "wrap",
    gap: "10px",
  },
  refreshBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    background: "#ffffff",
    color: "#64748b",
    cursor: "pointer",
    outline: "none",
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
  },
  th: {
    padding: "10px 12px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
    borderBottom: "2px solid #e2e8f0",
  },
  thStatus: {
    padding: "10px 12px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
    borderBottom: "2px solid #e2e8f0",
    width: "90px",
    textAlign: "center",
  },
  thActions: {
    padding: "10px 12px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
    borderBottom: "2px solid #e2e8f0",
    textAlign: "center",
    width: "120px",
  },
  tr: {
    borderBottom: "1px solid #f1f5f9",
    transition: "background 0.15s ease",
  },
  td: {
    padding: "12px",
    fontSize: "14px",
    color: "#64748b",
  },
  tdBold: {
    padding: "12px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#0f172a",
  },
  tdEmail: {
    padding: "12px",
    fontSize: "13px",
    color: "#64748b",
  },
  tdStatus: {
    padding: "12px",
    textAlign: "center",
  },
  statusBadge: {
    padding: "2px 8px",
    borderRadius: "9999px",
    fontSize: "11px",
    fontWeight: "600",
    textTransform: "capitalize",
    display: "inline-block",
  },
  tdActions: {
    padding: "12px",
    textAlign: "center",
  },
  skeletonBar: {
    height: "14px",
    background: "#e2e8f0",
    borderRadius: "4px",
    width: "100px",
    animation: "pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite",
  },
  overlayLoader: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.72)",
    backdropFilter: "blur(2px)",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: THEME,
    fontWeight: "600",
    fontSize: "13px",
    borderRadius: "8px",
  },
  loadingBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "40px",
    color: "#64748b",
    fontSize: "14px",
  },
  loader: {
    width: "20px",
    height: "20px",
    border: "2.5px solid #dbeafe",
    borderTop: "2.5px solid #2563eb",
    borderRadius: "50%",
    boxSizing: "border-box",
  },
  emptyBox: {
    padding: "40px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: "14px",
    background: "#f8fafc",
    borderRadius: "12px",
    border: "1px dashed #e2e8f0",
  },
  errorBox: {
    background: "#fee2e2",
    color: "#991b1b",
    padding: "12px 14px",
    borderRadius: "10px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  successBox: {
    background: "#dcfce7",
    color: "#15803d",
    padding: "12px 14px",
    borderRadius: "10px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(15, 23, 42, 0.4)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 99999,
    padding: "20px",
    boxSizing: "border-box",
  },
  modalContent: {
    background: "#ffffff",
    borderRadius: "16px",
    padding: "24px",
    maxWidth: "800px",
    width: "100%",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    maxHeight: "90vh",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  modalTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "700",
    color: "#0f172a",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "24px",
    color: "#94a3b8",
    cursor: "pointer",
    lineHeight: 1,
    padding: 0,
  },
  modalTableContainer: {
    width: "100%",
    overflowY: "auto",
    maxHeight: "50vh",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
  },
  modalCloseBtn: {
    padding: "8px 16px",
    background: THEME,
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    outline: "none",
  },
};
